import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Clock, Bell, BarChart2, FileCheck } from "lucide-react";

const SLAManagementUseCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="relative bg-[#0077bb] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <h1 className="text-4xl font-bold text-white mb-6">
                SLA Management Made Simple
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl">
                Track, monitor, and optimize your service level agreements with our comprehensive SLA management solution.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Streamline Your SLA Management
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Ensure service excellence with powerful SLA management tools:
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Monitor SLA compliance in real-time
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Track performance metrics
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Generate comprehensive reports
                </li>
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0"
                alt="SLA management"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>

          {/* Key Features Section */}
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-12">
                Key Features
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Real-time Monitoring</h3>
                  <p className="text-gray-600">
                    Track SLA performance in real-time with automated alerts.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Bell className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Alert System</h3>
                  <p className="text-gray-600">
                    Instant notifications for SLA breaches and critical events.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <BarChart2 className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Performance Analytics</h3>
                  <p className="text-gray-600">
                    Comprehensive reporting and analytics dashboard.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Common Use Cases Section */}
      <div className="w-full bg-gray-50">
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Service Management</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Response time tracking</li>
                <li>Service quality monitoring</li>
                <li>Customer support SLAs</li>
                <li>Performance measurement</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Vendor Management</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Vendor SLA compliance</li>
                <li>Service delivery tracking</li>
                <li>Performance reporting</li>
                <li>Contract management</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="py-20">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Optimize Your SLA Management?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Join organizations that have transformed their SLA management with our solution.
              </p>
              <div className="space-x-4">
                <Link to="/request-demo">
                  <Button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                    Request Demo
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default SLAManagementUseCase;
